import PropTypes from 'prop-types'
import R from 'ramda'
import styled from 'styled-components'
import { AnimatePresence } from 'framer-motion'
import { arrayMove } from '@dnd-kit/sortable'
import { createPortal } from 'react-dom'
import { useMedia } from 'use-media'
import { useEffect, useState } from 'react'

import {
  Box,
  Container,
  Heading,
  Image,
  Icon,
  HR,
  MonoButton,
  MotionBox,
  Text,
  mediaQueries,
} from '@redriverpress/system'

import { ManageFlashcards } from 'Packages/flashcards'

import useSelection from '../../hooks/useSelection'

const DrawerTop = styled(Box).attrs({
  bg: 'black',
  color: 'white',
  display: 'flex',
  alignItems: 'baseline',
  py: 2,
  px: 3,
  boxShadow: 4,
})`
  border-top-left-radius: ${props => props.theme.radii.rounded};
  border-top-right-radius: ${props => props.theme.radii.rounded};
`

const previewTileSize = '4rem'

const DrawerPreview = styled(Box).attrs(props => ({
  bg: 'black',
  color: 'white',
  display: 'grid',
  gridTemplateColumns: `repeat(${props.columns}, ${previewTileSize})`,
  justifyContent: 'space-between',
  p: 3,
  boxShadow: 4,
}))`
  border-bottom-left-radius: ${props => props.theme.radii.rounded};
  border-bottom-right-radius: ${props => props.theme.radii.rounded};
`

const RemoveIconOverlay = styled(Box).attrs({
  bg: 'rgba(0,0,0,0.5)',
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})`
  inset: 0;
  transition: 0.25s ease;
`

const RemoveIconWrapper = styled(Box).attrs({
  size: '2rem',
  background: 'white',
  borderRadius: 'circular',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})`
  transition: 0.15s ease-in;
`

const PreviewFlashcardButton = styled.button`
  appearance: none;
  border: none;
  border-radius: ${props => props.theme.radii.rounded};
  background: ${props => props.theme.colors.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: ${previewTileSize};
  height: ${previewTileSize};
  position: relative;

  ${RemoveIconOverlay} {
    opacity: 0;
  }

  ${RemoveIconWrapper} {
    transform: scale(0.666);
  }

  &:hover ${RemoveIconOverlay}, &:focus ${RemoveIconOverlay} {
    opacity: 1;
  }

  &:hover ${RemoveIconWrapper}, &:focus ${RemoveIconWrapper} {
    transform: scale(1);
  }
`

const DrawerContents = styled(Box).attrs({
  boxShadow: 4,
  maxHeight: '66vh',
  overflow: 'scroll',
})`
  border-bottom-left-radius: ${props => props.theme.radii.rounded};
  border-bottom-right-radius: ${props => props.theme.radii.rounded};
  -webkit-overflow-scrolling: touch;
`

export default function SelectionDrawer({
  onSaveAction,
  onPrintAction,
  deleteAllAction,
}) {
  const {
    selection,
    removeFromSelection,
    editSelectionUserTitle,
    setSelection,
  } = useSelection()

  const [drawerOpen, setDrawerOpen] = useState(false)

  const isLg = useMedia(mediaQueries.lg)

  const previewLimit = isLg ? 11 : 6
  const previewSelection = R.take(previewLimit, selection)

  useEffect(() => {
    // Close the drawer if the selection is cleared, so that it doesn't reopen
    // immediately when a new selection is made
    selection.length === 0 && setDrawerOpen(false)
  }, [selection])

  return createPortal(
    <AnimatePresence>
      {selection.length && (
        <MotionBox
          key='flashcardSelectionDrawer'
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 64 }}
          position='fixed'
          bottom='1rem'
          left='0'
          right='0'
          display='flex'
          justifyContent='center'
          zIndex={6}
        >
          <Container width={1}>
            <Box mx={[null, null, -2, -3]}>
              <DrawerTop>
                <Heading as='h6' mb={0}>
                  Unsaved Selection
                </Heading>
                {isLg && (
                  <Text fontSize={0} pl={3}>
                    {selection.length} flashcard{selection.length !== 1 && 's'}
                  </Text>
                )}
                <Box ml='auto'>
                  <MonoButton small onClick={onSaveAction}>
                    <Icon icon='images' pr={2} />
                    Save to Set…
                  </MonoButton>
                  <MonoButton small onClick={onPrintAction}>
                    <Icon icon='print' pr={2} />
                    Print…
                  </MonoButton>
                  <MonoButton small onClick={() => setDrawerOpen(x => !x)}>
                    <Icon icon='edit' pr={2} />
                    Manage
                    <Icon
                      icon={drawerOpen ? 'chevron-down' : 'chevron-up'}
                      pl={2}
                    />
                  </MonoButton>
                </Box>
              </DrawerTop>

              <HR borderTop='1px solid' borderColor='gray600' />

              {drawerOpen ? (
                <DrawerContents>
                  <ManageFlashcards
                    deleteAllAction={deleteAllAction}
                    flashcards={selection}
                    onRemove={id => removeFromSelection([id])}
                    onRename={editSelectionUserTitle}
                    onReorder={({ flashcards, oldIndex, newIndex }) =>
                      setSelection(arrayMove(flashcards, oldIndex, newIndex))
                    }
                  />
                </DrawerContents>
              ) : (
                <DrawerPreview columns={previewLimit + 1}>
                  {previewSelection.map(fc => (
                    <Box key={fc.id}>
                      <PreviewFlashcardButton
                        onClick={() => removeFromSelection([fc.id])}
                      >
                        <Image size='3.5rem' src={fc.thumbnail} alt='' />
                        <RemoveIconOverlay>
                          <RemoveIconWrapper>
                            <Icon icon='trash-alt' />
                          </RemoveIconWrapper>
                        </RemoveIconOverlay>
                      </PreviewFlashcardButton>
                    </Box>
                  ))}
                  {selection.length > previewSelection.length && (
                    <Box
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                      textAlign='center'
                      color='white'
                      fontSize={0}
                    >
                      +{selection.length - previewSelection.length}
                      <br />
                      more
                    </Box>
                  )}
                </DrawerPreview>
              )}
            </Box>
          </Container>
        </MotionBox>
      )}
    </AnimatePresence>,
    document.body
  )
}

SelectionDrawer.propTypes = {
  onPrintAction: PropTypes.func.isRequired,
  onSaveAction: PropTypes.func.isRequired,
}
